import { useAnalytics } from "@/lib/analytics";
import { authClient } from "@/lib/better-auth";
import { useTranslation } from "@/lib/i18n";
import { APP_URL } from "@/lib/urls";
import { cn } from "@/lib/utils";
import ArrowMoveUpLeftIcon from "@hugeicons/core-free-icons/ArrowMoveUpLeftIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import { useForm } from "@tanstack/react-form";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "./AuthProvider";

type LoginFormProps = {
  surface?: "modal" | "drawer";
};

const creatorAvatars = [
  "/creators/creator-1.webp",
  "/creators/creator-2.webp",
  "/creators/creator-3.webp",
  "/creators/creator-4.webp",
];

const ratingStars = ["star-1", "star-2", "star-3", "star-4", "star-5"];

type LoginMethod = "email" | "google" | "instagram";

type LastLoginCookie = {
  email: string;
  isComplete: true;
  method: LoginMethod;
};

type CompletedLoginCookieInput = Omit<LastLoginCookie, "isComplete">;

const LAST_LOGIN_COOKIE_NAME = "zapify_last_login";
const PENDING_LOGIN_METHOD_COOKIE_NAME = "zapify_pending_login_method";
const LAST_LOGIN_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const PENDING_LOGIN_METHOD_COOKIE_MAX_AGE = 15 * 60;

function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const { hostname } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return undefined;
  }

  if (hostname.endsWith("zapify.pro")) {
    return ".zapify.pro";
  }

  return undefined;
}

function isLoginMethod(value: unknown): value is LoginMethod {
  return value === "email" || value === "google" || value === "instagram";
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) return null;

  return cookie.slice(name.length + 1);
}

function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === "undefined") return;

  const domain = getCookieDomain();
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:";

  let cookie = `${name}=${encodeURIComponent(
    value,
  )}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;

  if (domain) {
    cookie += `; Domain=${domain}`;
  }

  if (secure) {
    cookie += "; Secure";
  }

  document.cookie = cookie;
}

function deleteCookie(name: string): void {
  setCookie(name, "", 0);
}

function setLastLoginCookie(value: CompletedLoginCookieInput): void {
  const email = normalizeEmail(value.email);
  if (!email) return;

  setCookie(
    LAST_LOGIN_COOKIE_NAME,
    JSON.stringify({
      email,
      isComplete: true,
      method: value.method,
    }),
    LAST_LOGIN_COOKIE_MAX_AGE,
  );
}

function getLastLoginCookie(): LastLoginCookie | null {
  const rawValue = getCookieValue(LAST_LOGIN_COOKIE_NAME);
  if (!rawValue) return null;

  try {
    const parsedValue = JSON.parse(decodeURIComponent(rawValue)) as {
      email?: unknown;
      isComplete?: unknown;
      method?: unknown;
    };

    if (
      typeof parsedValue.email !== "string" ||
      parsedValue.isComplete !== true ||
      !isLoginMethod(parsedValue.method)
    ) {
      return null;
    }

    const email = normalizeEmail(parsedValue.email);
    if (!email) return null;

    return {
      email,
      isComplete: true,
      method: parsedValue.method,
    };
  } catch {
    return null;
  }
}

function setPendingLoginMethodCookie(method: LoginMethod): void {
  setCookie(
    PENDING_LOGIN_METHOD_COOKIE_NAME,
    method,
    PENDING_LOGIN_METHOD_COOKIE_MAX_AGE,
  );
}

function deletePendingLoginMethodCookie(): void {
  deleteCookie(PENDING_LOGIN_METHOD_COOKIE_NAME);
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        width="16"
        height="16"
        x="4"
        y="4"
        rx="5"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" />
    </svg>
  );
}

function MetaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 32" className={className} aria-hidden="true">
      <path
        d="M9.8 25.4c-3.5 0-6.1-2.8-6.1-7.1 0-7.8 4.6-14.1 9.8-14.1 3 0 5.4 1.8 8.9 6.9l1.6 2.3 1.6-2.3c3.5-5.1 5.9-6.9 8.9-6.9 5.2 0 9.8 6.3 9.8 14.1 0 4.3-2.6 7.1-6.1 7.1-3.1 0-5.6-1.8-9.8-7.7l-4.4-6.3-4.4 6.3c-4.2 5.9-6.7 7.7-9.8 7.7Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4.2"
      />
    </svg>
  );
}

function VerifiedBadge({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#1877F2"
        d="m12 2 2.7 2 3.3-.1 1 3.1 2.7 2-1.1 3.1 1.1 3.1-2.7 2-1 3.1-3.3-.1-2.7 2-2.7-2-3.3.1-1-3.1-2.7-2 1.1-3.1-1.1-3.1 2.7-2 1-3.1 3.3.1L12 2Z"
      />
      <path
        d="m8.2 12 2.4 2.4 5.2-5.2"
        fill="none"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

export function LoginForm({ surface = "modal" }: LoginFormProps) {
  const { translations: t } = useTranslation();
  const { isReferralValid, verifyReferralCode, clearReferral, extraTrialDays } =
    useAuth();
  const { track } = useAnalytics();
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isInstagramLoading, setIsInstagramLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<
    "valid" | "invalid" | null
  >(null);
  const [lastLogin] = useState(() => getLastLoginCookie());
  const isDrawer = surface === "drawer";

  const hasValidReferral = !!(
    discountCode &&
    (isReferralValid || verificationResult === "valid")
  );

  useEffect(() => {
    const storedCode = localStorage.getItem("referralCode");
    if (storedCode) {
      setDiscountCode(storedCode);
      setShowDiscountInput(true);
      if (isReferralValid) {
        setVerificationResult("valid");
      }
    }
  }, [isReferralValid]);

  const emailForm = useForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      track("auth_email_otp_click", {
        email: value.email,
        section: "auth_form",
      });
      await authClient.emailOtp.sendVerificationOtp({
        email: value.email,
        type: "sign-in",
      });
      setIsOtpSent(true);
    },
  });

  const otpForm = useForm({
    defaultValues: {
      otp: "",
    },
    onSubmit: async ({ value }) => {
      const session = await authClient.signIn.emailOtp({
        email: emailForm.state.values.email,
        otp: value.otp,
      });
      if (session.data !== null) {
        track("auth_otp_verified", {
          email: emailForm.state.values.email,
          has_referral: hasValidReferral,
          section: "auth_form",
        });
        setLastLoginCookie({
          email: session.data.user.email ?? emailForm.state.values.email,
          method: "email",
        });
        deletePendingLoginMethodCookie();
        if (hasValidReferral) {
          localStorage.setItem("pendingReferral", discountCode);
          window.location.href = `${APP_URL}/app/onboarding?joinus=${discountCode}`;
        } else {
          window.location.href = `${APP_URL}/app/dashboard`;
        }
      }
    },
  });

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setPendingLoginMethodCookie("google");

    track("auth_google_click", {
      has_referral: hasValidReferral,
      section: "auth_form",
    });

    if (hasValidReferral) {
      localStorage.setItem("pendingReferral", discountCode);
    }
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: `${APP_URL}/app/dashboard`,
        newUserCallbackURL: hasValidReferral
          ? `${APP_URL}/app/onboarding?joinus=${discountCode}`
          : `${APP_URL}/app/onboarding`,
      });
    } catch {
      deletePendingLoginMethodCookie();
      setIsGoogleLoading(false);
    }
  };

  const handleInstagramLogin = async () => {
    setIsInstagramLoading(true);
    setPendingLoginMethodCookie("instagram");

    track("auth_instagram_click", {
      has_referral: hasValidReferral,
      section: "auth_form",
    });

    if (hasValidReferral) {
      localStorage.setItem("pendingReferral", discountCode);
    }
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const result = await authClient.signIn.oauth2({
        providerId: "instagram",
        callbackURL: `${APP_URL}/app/dashboard`,
        newUserCallbackURL: hasValidReferral
          ? `${APP_URL}/app/onboarding?joinus=${discountCode}`
          : `${APP_URL}/app/onboarding`,
        disableRedirect: true,
      });

      if (result.data?.url) {
        const url = new URL(result.data.url);
        window.location.href = url.toString();
      }
    } catch {
      deletePendingLoginMethodCookie();
      setIsInstagramLoading(false);
    }
  };

  const handleEmailLogin = () => {
    track("auth_continue_clicked", {
      section: "auth_form",
    });
    track("cta_clicked", {
      cta_id: "auth_email_login",
      button_text: "Continue with Email",
      section: "auth_form",
    });
    if (lastLogin?.method === "email") {
      emailForm.setFieldValue("email", lastLogin.email);
    }
    setShowEmailForm((value) => !value);
  };

  const handleDifferentEmail = () => {
    track("auth_different_email_clicked", {
      section: "auth_form",
    });
    setIsOtpSent(false);
  };

  return (
    <section
      aria-label="Meta Verified sign in"
      className={cn(
        "mx-auto w-full bg-white text-center text-[#050505] dark:bg-neutral-950 dark:text-neutral-50",
        isDrawer
          ? "max-w-[360px] px-3 pb-2 pt-3"
          : "max-w-[500px] rounded-[22px] px-6 pb-5 pt-7 sm:px-8",
      )}
    >
      {!isOtpSent && (
        <>
          <div
            className={cn(
              "mx-auto flex w-fit items-center justify-center rounded-full border border-[#F6E2B8]/20 bg-white text-[#FF9F0A] shadow-[0_10px_28px_rgba(245,158,11,0.06)] dark:border-amber-300/30 dark:bg-neutral-950",
              isDrawer ? "mb-3 h-9 gap-2 px-3.5" : "mb-5 h-12 gap-2.5 px-5",
            )}
          >
            <MetaIcon
              className={cn(
                isDrawer ? "h-5 w-8 text-[#007DF3]" : "h-7 w-10 text-[#007DF3]",
              )}
            />
            <span
              className={cn(
                "font-black leading-none tracking-normal text-[#050505] dark:text-neutral-50",
                isDrawer ? "text-sm" : "text-lg",
              )}
            >
              Meta Approved
            </span>
          </div>

          <div
            className={cn(
              "mx-auto flex items-center justify-center rounded-[24px] bg-transparent",
              isDrawer
                ? "mb-4 max-w-[310px] flex-col gap-1.5 px-3 py-2.5"
                : "mb-5 w-full max-w-[390px] px-3.5 py-2.5",
            )}
          >
            <div
              className={cn(
                "flex shrink-0",
                isDrawer ? "-space-x-3" : "-space-x-3",
              )}
              aria-hidden="true"
            >
              {creatorAvatars.map((src) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className={cn(
                    "rounded-full border-[3px] border-white object-cover dark:border-neutral-950",
                    isDrawer ? "size-8" : "size-9",
                  )}
                  loading="lazy"
                />
              ))}
            </div>
            <div
              className={cn(
                "w-px shrink-0 bg-[#E5E7EB] dark:bg-white/10",
                isDrawer ? "hidden" : "mx-3 h-9",
              )}
            />
            <div
              className={cn(
                "min-w-0",
                isDrawer
                  ? "mx-auto w-fit text-center"
                  : "w-[188px] shrink-0 text-left",
              )}
            >
              <div
                className="inline-flex w-fit items-center gap-[2px] whitespace-nowrap text-[#FF9F0A] leading-[1]"
                aria-label="4.9 out of 5 stars"
              >
                {ratingStars.map((star) => (
                  <span
                    key={star}
                    className={cn(
                      "leading-none",
                      isDrawer ? "text-[17px]" : "text-[18px]",
                    )}
                  >
                    ★
                  </span>
                ))}
                <span
                  className={cn(
                    "ml-2 font-extrabold leading-none text-[#050505] dark:text-neutral-50",
                    isDrawer ? "text-base" : "text-[17px]",
                  )}
                >
                  4.9/5
                </span>
              </div>
              <p
                className={cn(
                  "font-medium text-[#4B5563] dark:text-neutral-300",
                  isDrawer ? "mt-1 text-xs" : "mt-1 text-[13px]",
                )}
              >
                Trusted by 5K+ creators
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleInstagramLogin}
            disabled={isInstagramLoading}
            className={cn(
              "group relative mx-auto grid w-full items-center bg-primary-gradient px-2 text-primary-foreground transition duration-200 hover:-translate-y-px hover:brightness-[0.99] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25 disabled:cursor-wait disabled:opacity-75",
              isDrawer
                ? "h-[52px] max-w-[300px] grid-cols-[44px_1fr_44px] rounded-[16px]"
                : "h-14 max-w-[370px] grid-cols-[50px_1fr_50px] rounded-[17px]",
            )}
            aria-label="Continue with Instagram"
          >
            {lastLogin?.isComplete && lastLogin.method === "instagram" && (
              <span className="absolute right-3 top-1.5 rounded-full border border-black/10 bg-black/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-normal text-white/95 shadow-sm">
                Last used
              </span>
            )}
            {isInstagramLoading ? (
              <svg
                viewBox="0 0 24 24"
                className={cn(
                  "justify-self-center animate-spin",
                  isDrawer ? "size-6" : "size-7",
                )}
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="3"
                  className="opacity-30"
                />
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="3"
                  d="M21 12a9 9 0 0 0-9-9"
                />
              </svg>
            ) : (
              <InstagramIcon
                className={cn(
                  "justify-self-center transition duration-200 group-hover:scale-105",
                  isDrawer ? "size-6" : "size-7",
                )}
              />
            )}
            <span
              className={cn(
                "block min-w-0 max-w-full text-center font-extrabold leading-none",
                isDrawer ? "text-[15px]" : "text-[17px]",
              )}
            >
              {isInstagramLoading ? "Redirecting" : "Continue with Instagram"}
            </span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className={cn(
                "justify-self-center transition duration-200 group-hover:translate-x-0.5",
                isDrawer ? "size-6" : "size-7",
              )}
              aria-hidden="true"
            >
              <path
                d="M5 12h14m-6-6 6 6-6 6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
              />
            </svg>
          </button>

          <div
            className={cn(
              "mx-auto flex w-full items-center gap-2 pt-1 text-primary animate-in fade-in zoom-in-95 duration-500 delay-500 fill-mode-backwards",
              isDrawer
                ? "mb-3 mt-1.5 max-w-[300px] pl-4"
                : "mb-4 mt-1.5 max-w-[370px] pl-6",
            )}
          >
            <HugeiconsIcon
              icon={ArrowMoveUpLeftIcon}
              size={isDrawer ? 20 : 24}
              color="currentColor"
              className="rotate-75 scale-y-[-1]"
              aria-hidden
            />
            <span
              className={cn(
                "font-semibold leading-none text-shadow-[0_2px_4px_rgba(0,0,0,0.06)] rotate-2",
                isDrawer ? "text-[11px]" : "text-sm",
              )}
              style={{
                fontFamily:
                  "'Comic Sans MS', 'Bradley Hand', 'Segoe Print', 'Brush Script MT', cursive",
              }}
            >
              Fastest way to get started
            </span>
          </div>

          <div
            className={cn(
              "flex items-center justify-center",
              isDrawer ? "mb-3" : "mb-4",
            )}
          >
            <span className="h-px flex-1 bg-[#E5E7EB] dark:bg-white/10" />
            <span
              className={cn(
                "font-medium text-[#4B5563] dark:text-neutral-300",
                isDrawer ? "px-5 text-sm" : "px-5 text-base",
              )}
            >
              or
            </span>
            <span className="h-px flex-1 bg-[#E5E7EB] dark:bg-white/10" />
          </div>

          <div
            className={cn(
              "mx-auto grid w-full",
              isDrawer
                ? "mb-4 max-w-[300px] gap-2"
                : "mb-4 max-w-[370px] gap-2.5",
            )}
          >
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              className={cn(
                "relative grid w-full items-center border border-[#E5E7EB] bg-white px-2 font-extrabold text-[#050505] shadow-[0_8px_20px_rgba(15,23,42,0.045)] transition duration-200 hover:-translate-y-px hover:border-[#D1D5DB] hover:shadow-[0_10px_24px_rgba(15,23,42,0.06)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 disabled:cursor-wait disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.04] dark:text-neutral-50 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-white/[0.07] font-bold",
                isDrawer
                  ? "h-[52px] grid-cols-[44px_1fr_44px] rounded-[16px] text-[15px]"
                  : "h-14 grid-cols-[50px_1fr_50px] rounded-[17px] text-[17px]",
              )}
            >
              {lastLogin?.isComplete && lastLogin.method === "google" && (
                <span className="absolute right-3 top-1.5 rounded-full border border-[#E5E7EB] bg-[#050505]/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-normal text-white/95 shadow-sm dark:border-white/10">
                  Last used
                </span>
              )}
              {isGoogleLoading ? (
                <svg
                  viewBox="0 0 24 24"
                  className={cn(
                    "justify-self-center animate-spin",
                    isDrawer ? "size-5" : "size-6",
                  )}
                  aria-hidden="true"
                >
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 48 48"
                  className={cn(
                    "justify-self-center",
                    isDrawer ? "size-5" : "size-6",
                  )}
                  aria-hidden="true"
                >
                  <path
                    fill="#FFC107"
                    d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9Z"
                  />
                  <path
                    fill="#FF3D00"
                    d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7Z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44Z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.7-.4-3.9Z"
                  />
                </svg>
              )}
              <span>
                {isGoogleLoading ? "Redirecting" : "Continue with Google"}
              </span>
              <span />
            </button>

            <button
              type="button"
              onClick={handleEmailLogin}
              className={cn(
                "relative grid w-full items-center border border-[#E5E7EB] bg-white px-2 font-extrabold text-[#050505] shadow-[0_8px_20px_rgba(15,23,42,0.045)] transition duration-200 hover:-translate-y-px hover:border-[#D1D5DB] hover:shadow-[0_10px_24px_rgba(15,23,42,0.06)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 dark:border-white/10 dark:bg-white/[0.04] dark:text-neutral-50 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-white/[0.07] font-bold",
                isDrawer
                  ? "h-[52px] grid-cols-[44px_1fr_44px] rounded-[16px] text-[15px]"
                  : "h-14 grid-cols-[50px_1fr_50px] rounded-[17px] text-[17px]",
              )}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className={cn(
                  "justify-self-center",
                  isDrawer ? "size-5" : "size-[23px]",
                )}
                aria-hidden="true"
              >
                <rect
                  width="18"
                  height="14"
                  x="3"
                  y="5"
                  rx="3"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="m5 8 7 5 7-5"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
              <span>Continue with Email</span>
              <span />
            </button>
          </div>
        </>
      )}

      {showEmailForm && (
        <div className={cn("mx-auto text-left", isDrawer ? "mb-4 max-w-[300px]" : "mb-4 max-w-[370px]")}>
          {!isOtpSent ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                emailForm.handleSubmit();
              }}
              className="space-y-3 rounded-2xl border border-[#E5E7EB] bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <label
                htmlFor="email"
                className="text-xs font-semibold text-foreground"
              >
                {t.auth.emailAddress}
              </label>
              <emailForm.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return t.auth.emailRequired;
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                      return t.auth.validEmail;
                    }
                  },
                }}
              >
                {(field) => (
                  <>
                    <input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      className="h-11 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15 dark:border-white/10 dark:bg-neutral-950 dark:focus:border-primary"
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              </emailForm.Field>
              <emailForm.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
              >
                {([canSubmit, isSubmitting]) => (
                  <button
                    type="submit"
                    className="flex h-11 w-full items-center justify-center rounded-xl bg-primary-gradient px-4 text-sm font-bold text-primary-foreground transition hover:brightness-[0.97] disabled:cursor-wait disabled:opacity-70"
                    disabled={!canSubmit || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      t.auth.startFree
                    )}
                  </button>
                )}
              </emailForm.Subscribe>
            </form>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                otpForm.handleSubmit();
              }}
              className="space-y-3 rounded-2xl border border-[#E5E7EB] bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <label
                htmlFor="otp"
                className="text-xs font-semibold text-foreground"
              >
                {t.auth.enterVerificationCode}
              </label>
              <p className="text-xs text-muted-foreground">
                {t.auth.weSentCode}{" "}
                <span className="font-semibold text-foreground">
                  {emailForm.state.values.email}
                </span>
              </p>
              <otpForm.Field
                name="otp"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return t.auth.otpRequired;
                    if (!/^\d{6}$/.test(value)) {
                      return t.auth.otpDigits;
                    }
                  },
                }}
              >
                {(field) => (
                  <>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) => {
                        const value = event.target.value.replace(/\D/g, "");
                        if (value.length <= 6) {
                          field.handleChange(value);
                        }
                      }}
                      className="h-12 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 text-center font-mono text-lg tracking-[0.28em] text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15 dark:border-white/10 dark:bg-neutral-950 dark:focus:border-primary"
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </>
                )}
              </otpForm.Field>
              <otpForm.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
              >
                {([canSubmit, isSubmitting]) => (
                  <button
                    type="submit"
                    className="flex h-11 w-full items-center justify-center rounded-xl bg-primary-gradient px-4 text-sm font-bold text-primary-foreground transition hover:brightness-[0.97] disabled:cursor-wait disabled:opacity-70"
                    disabled={!canSubmit || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      t.auth.verifyAndContinue
                    )}
                  </button>
                )}
              </otpForm.Subscribe>
              <button
                type="button"
                onClick={handleDifferentEmail}
                className="flex h-9 w-full items-center justify-center gap-2 rounded-xl text-xs font-semibold text-muted-foreground transition hover:bg-white dark:hover:bg-white/[0.06]"
              >
                <ArrowLeft className="size-4" />
                {t.auth.useDifferentEmail}
              </button>
            </form>
          )}
        </div>
      )}

      {!isOtpSent && (
        <>
          <div
            className={cn(
              "flex items-center justify-center text-primary",
              isDrawer ? "text-[11px]" : "text-xs",
            )}
          >
            <span
              className={cn(
                "inline-flex items-center",
                isDrawer ? "gap-1.5" : "gap-2",
              )}
            >
              <span
                className={cn(
                  "grid place-items-center rounded-full border border-primary text-primary",
                  isDrawer ? "size-4" : "size-4",
                )}
              >
                <Check className="size-3" strokeWidth={2} />
              </span>
              Secure Meta login
            </span>
            <span
              className={cn(
                "w-px bg-[#E5E7EB] dark:bg-white/10",
                isDrawer ? "mx-3 h-5" : "mx-4 h-6",
              )}
            />
            <span
              className={cn(
                "inline-flex items-center",
                isDrawer ? "gap-1.5" : "gap-2",
              )}
            >
              <span
                className={cn(
                  "grid place-items-center rounded-full border border-primary text-primary",
                  isDrawer ? "size-4" : "size-4",
                )}
              >
                <Check className="size-3" strokeWidth={2} />
              </span>
              Disconnect anytime
            </span>
          </div>

          <p
            className={cn(
              "mx-auto text-center text-[#4B5563] dark:text-neutral-300",
              isDrawer
                ? "mt-3 text-[10px] leading-relaxed"
                : "mt-4 text-[11px] leading-relaxed",
            )}
          >
            By continuing, you agree to our{" "}
            <a
              href="https://zapify.pro/terms"
              className="font-medium text-primary transition hover:text-primary/80"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://zapify.pro/privacy"
              className="font-medium text-primary transition hover:text-primary/80"
            >
              Privacy Policy
            </a>
            .
          </p>

          <div
            className={cn(
              "mx-auto text-center",
              isDrawer ? "mt-3 max-w-[300px]" : "mt-4 max-w-[370px]",
            )}
          >
            {!showDiscountInput && (
              <button
                type="button"
                onClick={() => setShowDiscountInput(true)}
                className={cn(
                  "text-xs font-semibold text-[#4B5563] underline-offset-4 transition hover:text-[#050505] hover:underline dark:text-neutral-300 dark:hover:text-neutral-50",
                )}
              >
                {t.auth.haveDiscountCode}
              </button>
            )}

            {showDiscountInput && (
              <div
                className={cn(
                  "rounded-2xl border border-[#E5E7EB] bg-white p-3 text-left shadow-[0_8px_20px_rgba(15,23,42,0.045)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none",
                )}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">
                    {t.auth.discountCode}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      clearReferral();
                      setDiscountCode("");
                      setVerificationResult(null);
                      setShowDiscountInput(false);
                    }}
                    className="text-muted-foreground transition hover:text-foreground"
                    aria-label="Close discount code"
                  >
                    <X className="size-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <div className="relative min-w-0 flex-1">
                    <input
                      value={discountCode}
                      onChange={(event) => {
                        setDiscountCode(event.target.value);
                        setVerificationResult(null);
                      }}
                      placeholder={t.auth.enterCode}
                      className={cn(
                        "h-10 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 pr-14 text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15 dark:border-white/10 dark:bg-neutral-950 dark:focus:border-primary",
                        isDrawer ? "text-base" : "text-sm",
                      )}
                    />
                    {verificationResult === "valid" && (
                      <Check className="absolute right-8 top-1/2 size-4 -translate-y-1/2 text-green-600 dark:text-green-400" />
                    )}
                    {discountCode && (
                      <button
                        type="button"
                        onClick={() => {
                          clearReferral();
                          setDiscountCode("");
                          setVerificationResult(null);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                        aria-label="Clear referral code"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="h-10 rounded-xl bg-primary-gradient px-4 text-xs font-bold text-primary-foreground transition hover:brightness-[0.97] disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={
                      isVerifying ||
                      !discountCode.trim() ||
                      verificationResult === "valid"
                    }
                    onClick={async () => {
                      if (discountCode.trim()) {
                        setIsVerifying(true);
                        const isValid = await verifyReferralCode(
                          discountCode.trim(),
                        );
                        setVerificationResult(isValid ? "valid" : "invalid");
                        setIsVerifying(false);
                      }
                    }}
                  >
                    {isVerifying ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : verificationResult === "valid" ? (
                      t.auth.applied
                    ) : (
                      t.auth.apply
                    )}
                  </button>
                </div>
                {verificationResult && (
                  <p className="mt-2 text-xs">
                    {verificationResult === "valid" ? (
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {extraTrialDays > 0
                          ? t.auth.extendedTrialBannerText.replace(
                              "{days}",
                              String(30 + extraTrialDays),
                            )
                          : t.auth.congratsDiscount}
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">
                        {t.auth.invalidCode}
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
